import { fn } from '@ember/helper';
import { not, or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import CardsGeneric from '../../cards/generic.gts';
import Component from '@glimmer/component';
import CourseCardOverviewTable from './course-card/overview-table.gts';
import CustomFieldsApproverReview from '../../custom-fields-approver-review.gts';
import { divide } from 'tio-ui/math';
import ProgramReadonlyField from '../../program-readonly-field.gts';
import safeFormatNumber from '../../../../helpers/safe-format-number.ts';
import { t } from 'ember-intl';
import type TasCourseModel from 'tio-data/models/tas-course';
import type { TASCourseModelFieldsSignature } from 'tio-data/models/tas-course';
import { PhCaretDown, PhControl } from 'ember-phosphor-icons';

const number = Number;

interface TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCardSignature {
  Args: {
    course: TasCourseModel;
    showCustomFields?: boolean;
  };
}

export default class TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCardComponent extends Component<TuitionAssistanceProgramDetailsAppDetailsReadonlyCourseCardSignature> {
  @tracked isExpanded = false;

  // TAS.TODO - Custom course expense fields
  get courseExpenseFields(): { name: keyof TASCourseModelFieldsSignature; mask: string }[] {
    return [
      { name: 'COURSE_TUITION', mask: 'currency' },
      { name: 'COURSE_BOOKS', mask: 'currency' },
      { name: 'COURSE_LABS', mask: 'currency' },
      { name: 'COURSE_SOFTWARE', mask: 'currency' },
      { name: 'COURSE_FEES', mask: 'currency' },
    ];
  }

  <template>
    <CardsGeneric>
      <:headerTitle>
        {{@course.displayName}}
      </:headerTitle>
      <:body>
        <CourseCardOverviewTable @course={{@course}} />

        {{#if this.isExpanded}}
          <div class="my-4">
            <p class="font-semibold mb-1">
              {{t "tuition_assistance.program_details.courses.course_expenses_breakdown"}}
            </p>
            <dl class="grid grid-cols-2 gap-1 text-sm">
              {{#each this.courseExpenseFields as |expenseField|}}
                <ProgramReadonlyField
                  {{! @glint-expect-error: not sure}}
                  @name={{expenseField.name}}
                  @programTemplate={{@course.tasApplication.tasProgramInstance.tasProgramTemplate}}
                  @fields={{@course.fields}}
                  as |field|
                >
                  <dt>{{field.programLabel}}</dt>
                  <dd class="text-right">
                    {{safeFormatNumber
                      (divide (or (number field.value) 0) 100)
                      style="currency"
                      currency="USD"
                    }}
                  </dd>
                </ProgramReadonlyField>
              {{/each}}
            </dl>
          </div>
          <CustomFieldsApproverReview
            @customFields={{@course.customFields}}
            @assets={{@course.tasAssets}}
            @province="COURSE"
          />
        {{/if}}
      </:body>
      <:footer>
        <div class="tio-flex-center">
          <button
            type="button"
            class="tio-text-sm-semibold tio-fg-secondary"
            {{on "click" (fn (mut this.isExpanded) (not this.isExpanded))}}
          >
            {{#if this.isExpanded}}
              <PhCaretDown @size="32" class="align-middle" />
            {{else}}
              <PhControl @size="32" class="align-middle" />
            {{/if}}
            {{t "tuition_assistance.program_details.courses.view_course_details_and_attachments"}}
          </button>
        </div>
      </:footer>
    </CardsGeneric>
  </template>
}
